import type {
  CoverData,
  CoverDataSlot,
  DesignTemplateCategory,
  DesignTemplateSummary,
} from '@mathflat/domain/DesignTemplate'
import {
  adjustSpineWithPageSize,
  getAllCoverItems,
} from '@mathflat/shared/@modules/coverData/coverData'
import type { WithAllKR } from '@mathflat/shared/@types/utilityTypes'
import type { SVGTextProps } from '@mathflat/visual-editor'
import { makeAutoObservable, observable } from 'mobx'

import { TOAST_STR } from '@/constant'
import { ERROR_CODE } from '@/constant/errorCode'
import type {
  CoverDataFieldValues,
  CoverDataTextFormFieldValues,
} from '@/contentComponents/CoverEditor/CoverEditorTemplate'
import { designTemplateController } from '@/controller/designTemplate.controller'
import { podMallController } from '@/controller/podMall.controller'
import type { WorkbookDesignTemplate } from '@/dto/DesignTemplate/WorkbookCoverDesignTemplate'
import { commonErrorMessage, toastError, toastSuccess } from '@/utils/toast'

export const 미색모조_MM_PER_PAGE = 0.09
export const FIGMA_A4_WIDTH = 620
export const FIGMA_A4_HEIGHT = 877
export class TextFormValue {
  value: NonNullable<SVGTextProps['defaultValue']>
  fontFamily: SVGTextProps['renderProps']['fontFamily']
  fontSize: SVGTextProps['renderProps']['fontSize']
  maxFontSize: SVGTextProps['renderProps']['maxFontSize']
  constructor({ value, fontFamily, fontSize, maxFontSize }: TextFormValue) {
    this.value = value
    this.fontFamily = fontFamily
    this.fontSize = fontSize
    this.maxFontSize = maxFontSize
  }
}

export interface IDesignTemplateCoverEditorController {
  selectedCoverDataItemId: CoverDataSlot | null
  selectCoverDataItemId: (coverDataItemId: CoverDataSlot | null) => void
}

export class DesignTemplateService<U extends WorkbookDesignTemplate> {
  // 현재 선택된 표지 템플릿 - 왼쪽 패널에 렌더링
  currentDesignTemplate: U | null = null
  // 즐겨찾는 표지 템플릿 (Summary)
  private _favoriteDesignTemplates: DesignTemplateSummary[] = []
  // 최근 선택한 표지 템플릿 (Summary)
  private _recentDesignTemplates: (DesignTemplateSummary & {
    category: WithAllKR<DesignTemplateCategory>
  })[] = []

  // 표지 선택 패널 - 표지 리스트 필터: 주제
  selectedCategory: WithAllKR<DesignTemplateCategory> = '전체'

  // coverData 없는 가벼운 리스트
  designTemplateSummaries = {
    SKY: [],
    BLUE_GREEN: [],
    GREEN: [],
    PINK: [],
    ORANGE: [],
    YELLOW: [],
    WHITE: [],
    BW: [],
  } as Record<DesignTemplateSummary['color'], DesignTemplateSummary[]>

  // 표지 detail fetch시 저장해두는 coverData 있는 무거운 리스트
  private _cachedDesignTemplates = {
    SKY: [],
    BLUE_GREEN: [],
    GREEN: [],
    PINK: [],
    ORANGE: [],
    YELLOW: [],
    WHITE: [],
    BW: [],
  } as Record<U['color'], U[]>

  private _fetchDesignTemplate: (id: U['id']) => Promise<U>
  private _fetchDesignTemplateSummaries: (
    color: DesignTemplateSummary['color'],
  ) => Promise<DesignTemplateSummary[]>

  constructor({
    fetchDesignTemplate,
    fetchDesignTemplateSummaries,
  }: {
    fetchDesignTemplate: DesignTemplateService<U>['_fetchDesignTemplate']
    fetchDesignTemplateSummaries: DesignTemplateService<U>['_fetchDesignTemplateSummaries']
  }) {
    makeAutoObservable(this, {
      currentDesignTemplate: observable.ref,
    })

    this._fetchDesignTemplate = fetchDesignTemplate
    this._fetchDesignTemplateSummaries = fetchDesignTemplateSummaries
    this.fetchFavoriteDesignTemplates()
  }

  private get _flattenDesignTemplates(): U[] {
    return [...Object.values(this._cachedDesignTemplates)].flat() as U[]
  }

  private _getCachedDesignTemplateById(id: U['id']) {
    return this._flattenDesignTemplates.find((designTemplate) => designTemplate.id === id)
  }

  async fetchDesignTemplateSummaries(color: DesignTemplateSummary['color']) {
    if (this.designTemplateSummaries[color].length > 0) {
      return this.designTemplateSummaries[color]
    }

    const summaries = await this._fetchDesignTemplateSummaries(color)
    this.designTemplateSummaries[color] = summaries
    return summaries
  }

  fetchAndGetDesignTemplateById = async (id: U['id']) => {
    try {
      const cachedDesignTemplate = this._getCachedDesignTemplateById(id)

      if (!cachedDesignTemplate) {
        const designTemplate = await this._fetchDesignTemplate(id)
        this._cachedDesignTemplates[designTemplate.color].push(designTemplate)

        return designTemplate
      }

      return cachedDesignTemplate
    } catch (e) {
      commonErrorMessage(e)
      throw e
    }
  }

  setCurrentTemplate = (template: U) => {
    this.currentDesignTemplate = template
  }

  // --recent 로직--
  get recentDesignTemplates() {
    return this._recentDesignTemplates
  }

  addRecentDesignTemplate = (template: DesignTemplateSummary) => {
    this._recentDesignTemplates.unshift({ ...template, category: this.selectedCategory })
    if (this._recentDesignTemplates.length > 50) {
      this._recentDesignTemplates.pop()
    }
  }

  // --favorite 로직--
  get favoriteDesignTemplates() {
    return this._favoriteDesignTemplates
  }

  private fetchFavoriteDesignTemplates = async () => {
    this._favoriteDesignTemplates = await designTemplateController.getFavoriteDesignTemplate()
  }

  private async addFavoriteTemplate(templateId: U['id']) {
    await designTemplateController.postFavoriteDesignTemplate(templateId)
    toastSuccess(TOAST_STR.addFavorite)
    const template = await this.fetchAndGetDesignTemplateById(templateId)
    this.favoriteDesignTemplates.unshift(template.toSummary())
  }

  private async removeFavoriteTemplate(templateId: U['id']) {
    this._favoriteDesignTemplates = this._favoriteDesignTemplates.filter(
      (template) => template.id !== templateId,
    )
    await designTemplateController.deleteFavoriteDesignTemplate(templateId)
    toastSuccess(TOAST_STR.deleteFavorite)
  }

  private handleFavoriteError(e) {
    if (e?.code === ERROR_CODE.FAVORITE_DESIGN_TEMPLATE_LIMIT_EXCEEDED) {
      toastError(TOAST_STR.favoriteTemplateMax)
      return true
    }
    commonErrorMessage(e)
    return false
  }

  toggleFavoriteTemplate = async (templateId: U['id']) => {
    try {
      const hasTemplate = this.favoriteDesignTemplates.some(
        (template) => template.id === templateId,
      )
      if (hasTemplate) {
        await this.removeFavoriteTemplate(templateId)
      } else {
        await this.addFavoriteTemplate(templateId)
      }
    } catch (e) {
      if (this.handleFavoriteError(e)) return
      throw e
    }
  }

  // --static 멤버들--

  static printAdjustOptionMm?: number

  private static _fetchSetPrintOption = async () => {
    try {
      const data = await podMallController.getPrintOption()
      this.printAdjustOptionMm = data.pixelMm
      return this.printAdjustOptionMm
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  static async preProcessCoverDataAndSetCoverDataValueWithFormValue({
    coverData,
    formValues,
    pageSize,
    setFormValue,
  }: {
    coverData: CoverData
    formValues: CoverDataFieldValues
    pageSize: WorkbookDesignTemplate['pageSize']
    setFormValue: (
      pathKey:
        | `${keyof CoverDataTextFormFieldValues}.fontFamily`
        | `${keyof CoverDataTextFormFieldValues}.fontSize`
        | `${keyof CoverDataTextFormFieldValues}.maxFontSize`,
      value: string | number | undefined,
    ) => void
  }) {
    if (!DesignTemplateService.printAdjustOptionMm) {
      await DesignTemplateService._fetchSetPrintOption()
    }

    adjustSpineWithPageSize({ coverData, pageSize })

    getAllCoverItems(coverData).forEach((coverDataItem) => {
      if (coverDataItem.type === 'TEXT' && coverDataItem.id in formValues) {
        const id = coverDataItem.id as keyof CoverDataTextFormFieldValues
        // 부제목·학원명 칸은 폼 값이 null 일 수 있다
        const field = formValues[id]
        if (!field) {
          return
        }
        coverDataItem.props.defaultValue = field.value
        setFormValue(`${id}.fontFamily`, coverDataItem.props.renderProps.fontFamily)
        setFormValue(`${id}.fontSize`, coverDataItem.props.renderProps.fontSize)
        setFormValue(`${id}.maxFontSize`, coverDataItem.props.renderProps.maxFontSize)
      }
    })

    return coverData
  }

  static replaceCoverData = (template: WorkbookDesignTemplate, coverData: CoverData) => {
    template.setCoverData(coverData)
  }
}
